import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { MessageCircle, Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/utils/imageUrl';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const InstructorsPage = () => {
  const { settings } = useSiteSettings();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/instructors`);
        setInstructors(res.data);
      } catch (e) {
        console.error('Failed to fetch instructors:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Parse description into bullet points (split by 。)
  const descToHighlights = (desc) => {
    if (!desc) return [];
    return desc.split(/[。！]/).map(s => s.trim()).filter(s => s.length > 0);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
            星級名師
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
            經驗豐富、用心教學、幫助您順利考取駕照
          </p>
          <p className="text-base text-blue-200/80 mt-4">（師傅眾多，未能盡錄）</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="mt-4 text-muted-foreground">載入中...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {instructors.map((instructor) => {
                const highlights = descToHighlights(instructor.description);
                return (
                  <Card
                    key={instructor.id}
                    className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                    data-testid={`instructor-card-${instructor.id}`}
                  >
                    <div className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600" />
                    <CardContent className="p-0">
                      <div className="flex justify-center pt-6">
                        <img
                          src={resolveImageUrl(instructor.image_url) || '/images/tutors/placeholder.jpg'}
                          alt={instructor.name}
                          className="w-28 h-28 rounded-full object-cover border-4 border-blue-100 shadow-md"
                        />
                      </div>
                      <div className="px-6 pt-4 pb-6 text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{instructor.name}</h3>
                        {instructor.title && (
                          <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                            {instructor.title.split(/\s*\/\s*/).map((t, i) => (
                              <span key={i} className="text-xs px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                                {t.trim()}
                              </span>
                            ))}
                          </div>
                        )}

                        {highlights.length > 0 && (
                          <ul className="text-left space-y-2 mb-4">
                            {highlights.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {instructor.specialties && instructor.specialties.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 justify-center mb-3">
                            {instructor.specialties.slice(0, 4).map((s, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                                {s}
                              </span>
                            ))}
                          </div>
                        )}

                        {instructor.experience_years > 0 && (
                          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                            <Globe className="h-3 w-3" />
                            {instructor.experience_years}+ 年經驗
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold mb-4">想了解更多？</h2>
            <p className="text-lg text-muted-foreground mb-8">
              歡迎透過 WhatsApp 聯絡我們，安排合適的導師
            </p>
            <Button asChild size="lg" className="rounded-full bg-green-500 hover:bg-green-600 text-white px-8">
              <a href={`https://wa.me/${settings?.whatsapp || '85295858258'}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp 查詢
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InstructorsPage;
